import { useCallback, useState } from "react";
import { Check, ClipboardCopy } from "lucide-react";

interface CodeBlockProps {
  children: string;
  label?: string;
}

/**
 * Code snippet with a copy-to-clipboard button.
 *
 * The button shows an explicit "Copied" confirmation for ~2s after a copy.
 * Without that feedback the click mutates nothing in the DOM, so session
 * replay tooling (GlitchReplay/Sentry) flags it as a "dead click" — and
 * users genuinely can't tell whether the copy worked.
 */
export function CodeBlock({ children, label }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children);
    } catch {
      // Fallback for browsers/contexts without the async clipboard API.
      const ta = document.createElement("textarea");
      ta.value = children;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* nothing more we can do */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [children]);

  return (
    <div className="relative group">
      {label && (
        <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mb-1">
          {label}
        </div>
      )}
      <pre className="bg-slate-900 text-slate-100 text-sm rounded-lg p-4 overflow-x-auto">
        <code>{children}</code>
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        className={`absolute top-2 right-2 flex items-center gap-1 p-1.5 rounded-md transition-all ${
          copied
            ? "bg-emerald-600/90 text-white opacity-100"
            : "bg-slate-700/50 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100"
        }`}
        title={copied ? "Copied!" : "Copy"}
        aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
      >
        {copied ? <Check size={14} /> : <ClipboardCopy size={14} />}
        {copied && <span className="text-[10px] font-medium">Copied</span>}
      </button>
    </div>
  );
}
